import styles from '../Account.module.css';
import AccountCard from '../AccountCard';
import Boosters from '../../pricing/Boosters';
import SectionHead from '../SectionHead';
import StatusBadge from '../StatusBadge';
import EmptyState from '../EmptyState';
import Icon from '../Icon';
import { formatDate, formatNumber } from '../../../services/billingService';

// Plain-language explanation of what consumes each metered category, so the breakdown is
// self-explanatory. Keyed by the usage row `key` (see billingService PLAN_LIMITS).
const USAGE_EXPLAIN = {
  leads:
    '1 per net-new verified contact we deliver - from a chat hunt, a company CSV pull, or a named-contacts file. Everything inside the hunt (record unlocks, email + employment verification, website/HQ lookups, AI drafting) is bundled in, so nothing is double-counted.',
  verify:
    '1 per email you ask us to verify - a single address in chat, or a CSV of addresses you already have. Verifications that happen inside a hunt are part of the lead credit and never count here.',
  enrich:
    '1 per company row we return data for when you drop a company CSV and ask to enrich (a Not Found row is free). The columns you pick decide which sources we call, not what it costs.',
  website:
    '1 per company row we resolve to the correct website when the URL is missing, dead, or wrong. Rows that already have a working URL - and searches that find nothing - are free.',
  ai:
    'Unlimited. Chat questions, research answers, ICP updates, single and batch drafts, LinkedIn research summaries, and the AI that parses your commands.',
};

/**
 * Usage metrics - a per-category breakdown of what's been used vs. what's left this period.
 * Each of the five categories (leads, email verifications, company enrichments, website
 * lookups, AI) shows a live meter plus a plain-language note on what consumes it. Numbers
 * come from the authoritative server balances (billing.usage); nothing is fabricated.
 */
export default function UsageSection({ billing, onChoosePlan, onManagePlan }) {
  const { subscription: sub, usage = [], usageResetsAt, status, credits } = billing || {};
  const creditsLive = credits?.available;

  // Only truly empty when there's NO plan AND NO live lead credits. A trial user without
  // a plan still has their 30 Hunter credits to track, so we fall through to the meter.
  if (!sub && !creditsLive) {
    return (
      <>
        <SectionHead title="Usage" subtitle="Track your monthly credit usage and what's remaining." />
        <AccountCard title="Usage" description="Usage is tied to your subscription plan.">
          <EmptyState
            icon={<Icon name="grid" size={20} />}
            title="No usage to show yet"
            description="Choose a plan to start tracking your leads, verifications, enrichments and website lookups."
            action={<button className={styles.btnPrimary} onClick={onChoosePlan}>Choose a plan</button>}
          />
        </AccountCard>
      </>
    );
  }

  const resetDate = usageResetsAt || credits?.periodEnd || null;
  const onTrialNoPlan = !sub && creditsLive && !credits?.unlimited;
  // Header must quote the SAME number the leads row below renders - deriving both from
  // one source prevents a "30 in the header, 150 in the row" style mismatch.
  const leadsRow = usage.find((u) => u.key === 'leads');
  const leadsAllowance = leadsRow && !leadsRow.unlimited ? leadsRow.limit : credits?.allowance;

  return (
    <>
      <SectionHead title="Usage" subtitle="Track your monthly credit usage and what's remaining." />

      <AccountCard
        title="Usage this period"
        description={
          onTrialNoPlan
            ? `Your free trial includes ${leadsAllowance} lead credits${resetDate ? `, valid until ${formatDate(resetDate)}` : ''}.`
            : resetDate ? `Your allowances reset on ${formatDate(resetDate)}.` : 'Allowances reset at the start of each billing period.'
        }
        actions={<StatusBadge status={status} />}
      >
        {usage.map((m) => (
          <div className={styles.usageRow} key={m.key}>
            <div className={styles.usageTop}>
              <span className={styles.usageName}>
                <span className={styles.usageNameIcon}><Icon name={m.icon} size={13} /></span>
                {m.label}
              </span>
              {/*
                The ambiguity here was never about WHICH number - it was about the number
                standing alone.

                This read `121 / 150 / mo`, with "used" only in smaller text underneath, and was
                misread as a balance by the person who built the page. Switching it to
                `25 left of 150` just inverted the same trap: read quickly, `25 / 150` looks like
                25 spent. A bare `N / M` above a plan quota can be read either way.

                So: keep the number people asked to see - what they have spent - and put the word
                in the headline where it cannot be skipped. Same data, no new state.
              */}
              <span className={styles.usageLimit}>
                {m.unlimited ? (
                  <span className={styles.usageBadgeUnlimited}>Unlimited</span>
                ) : (
                  <><b>{formatNumber(m.used)}</b> used of {formatNumber(m.limit)}{m.unit ? ` ${m.unit}` : ''}</>
                )}
              </span>
            </div>

            {!m.unlimited && (
              <>
                <div className={styles.usageTrack}>
                  <div className={styles.usageFill} style={{ width: `${m.percent}%` }} />
                </div>
                <div className={styles.usageMeterFoot}>
                  <span><b>{formatNumber(m.used)}</b> used</span>
                  <span className={styles.usageRemaining}><b>{formatNumber(m.remaining)}</b> remaining</span>
                </div>
                {/* Called out separately because it behaves differently: the monthly
                    allowance resets, a booster does not. Folding it into one number left a
                    customer who had just paid unable to see what they had bought. */}
                {m.boosterBalance > 0 && (
                  <div className={styles.usageBooster}>
                    <Icon name="zap" size={12} />
                    <span>
                      Includes <b>{formatNumber(m.boosterBalance)}</b> booster
                      {m.boosterBalance === 1 ? '' : ' credits'} that never expire
                    </span>
                  </div>
                )}
              </>
            )}

            {USAGE_EXPLAIN[m.key] && (
              <p className={styles.usageExplain}>{USAGE_EXPLAIN[m.key]}</p>
            )}
          </div>
        ))}

        <div className={styles.trustLine}>
          <span className={styles.trustIcon} aria-hidden="true"><Icon name="clock" size={13} /></span>
          {creditsLive
            ? 'Your lead credits, email verifications and website lookups update in real time as you work. AI drafting and chat are unlimited.'
            : 'Usage updates in real time once your account is live.'}
        </div>
      </AccountCard>

      <AccountCard
        title="Need more?"
        description={sub ? 'Upgrade your plan for higher monthly limits across every metric.' : 'Choose a plan to unlock higher monthly limits.'}
        actions={<button className={`${styles.btnGhost} ${styles.btnSmall}`} onClick={sub ? onManagePlan : onChoosePlan}>{sub ? 'Manage plan' : 'Choose a plan'}</button>}
      >
        <p className={styles.note} style={{ marginTop: 0 }}>
          {sub
            ? 'Running low before your reset date? Moving up a plan raises your leads, email verifications, company enrichments and website lookups immediately.'
            : 'Your trial gives you a taste of RepBuddy. Choosing a plan raises your monthly leads, email verifications, company enrichments and website lookups.'}
        </p>
      </AccountCard>

      {/* Boosters, always available. They previously lived only on the marketing pricing
          page, so the moment a user actually wants one - looking at a meter that is nearly
          empty, mid-month - was the one place they could not buy it. A one-off top-up is
          also the cheaper answer than a plan upgrade for a single busy month, and hiding it
          pushed people toward the more expensive option. */}
      <AccountCard
        title="Add a booster"
        description="A one-off top-up for a busy month. Boosters never expire and roll over with you."
      >
        <Boosters compact />
        <div className={styles.trustLine}>
          <span className={styles.trustIcon} aria-hidden="true"><Icon name="zap" size={13} /></span>
          Booster credits sit on top of your monthly allowance and are only used once the
          monthly allowance runs out, so nothing you buy is ever wasted.
        </div>
      </AccountCard>
    </>
  );
}
